import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Validates a redirect target (post-login callback, post-checkout returnTo) to
 * prevent open redirects.
 *
 * A raw prefix check is NOT sufficient. The WHATWG URL parser strips ASCII
 * tab/LF/CR before parsing and treats "\" as "/" in special schemes, so both
 * "/\evil.com" and "/<TAB>/evil.com" resolve to https://evil.com/ while passing
 * `startsWith("/") && !startsWith("//")`. Resolving against a throwaway origin
 * and requiring the result to stay on it closes the whole class rather than
 * blocklisting one character at a time.
 *
 * Returns a same-origin path (pathname + search + hash), or `fallback`.
 */
export function safeRedirect(url: string | null | undefined, fallback = "/"): string {
  if (!url) return fallback;
  // Relative paths only — never a scheme, never an authority.
  if (!url.startsWith("/")) return fallback;
  try {
    const base = "https://safe-redirect.invalid";
    const resolved = new URL(url, base);
    if (resolved.origin !== base) return fallback;
    return `${resolved.pathname}${resolved.search}${resolved.hash}`;
  } catch {
    return fallback;
  }
}
